interface BeforeAfterSectionProps {
  onOpenForm: () => void;
}

const before = [
  'Outdated design that erodes trust',
  'Not built for AI or voice search',
  'Zero local SEO — invisible on Maps',
  'Generic content, no conversion intent',
  'Slow load speed hurts rankings',
  'SEO is an afterthought, not a strategy',
  'Built it once, never touched again',
];

const after = [
  'Modern 2026 design that commands authority',
  'Structured for AI tools, voice & smart speakers',
  'Optimized for Maps, local & "near me" searches',
  'Every word written to convert visitors to leads',
  'Sub-2-second load — fast by design',
  'SEO built into every page from the start',
  'Continuously improved month after month',
];

const differentiators = [
  {
    icon: 'ri-stack-line',
    title: 'Complete Growth System',
    desc: 'Not just SEO. Not just a website. A fully integrated system where design, strategy, and search work together.',
  },
  {
    icon: 'ri-search-eye-line',
    title: 'Built for Modern Search',
    desc: 'Most agencies are still optimizing for 2019. We build for 2026 — AI tools, voice, browser, and local maps.',
  },
  {
    icon: 'ri-focus-3-line',
    title: 'Conversion-First Approach',
    desc: 'Traffic without conversions is noise. Every element of your site is engineered to turn visitors into customers.',
  },
  {
    icon: 'ri-loop-right-line',
    title: 'Continuous Optimization',
    desc: 'We don\'t build and disappear. Monthly improvements compound your results — so your growth never stagnates.',
  },
];

export default function BeforeAfterSection({ onOpenForm }: BeforeAfterSectionProps) {
  return (
    <section className="bg-white py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-emerald-500 text-xs font-bold tracking-widest uppercase mb-4">The Difference</p>
          <h2 className="text-3xl md:text-5xl font-black text-[#0d1117] leading-tight mb-4">
            Typical Website vs.{' '}
            <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
              Our Website
            </span>
          </h2>
          <p className="text-[#6b7280] text-base md:text-lg max-w-lg mx-auto">
            This isn't just a redesign. It's the difference between a digital brochure and a revenue engine.
          </p>
        </div>

        {/* Before / After comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-16">
          {/* Before */}
          <div className="bg-[#fef2f2] border border-[#fecaca] rounded-2xl p-7 md:p-8">
            <div className="flex items-center gap-2.5 mb-6">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-100">
                <i className="ri-close-line text-[#ef4444]" style={{ fontSize: '18px' }}></i>
              </div>
              <p className="text-[#dc2626] text-sm font-bold uppercase tracking-widest">Typical Website</p>
            </div>
            <div className="space-y-3.5">
              {before.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-close-circle-line text-[#ef4444]" style={{ fontSize: '16px' }}></i>
                  </span>
                  <span className="text-[#374151] text-sm leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* After */}
          <div className="bg-[#f0fdf4] border border-[#86efac] rounded-2xl p-7 md:p-8">
            <div className="flex items-center gap-2.5 mb-6">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100">
                <i className="ri-check-line text-emerald-600" style={{ fontSize: '18px' }}></i>
              </div>
              <p className="text-emerald-700 text-sm font-bold uppercase tracking-widest">Our Website</p>
            </div>
            <div className="space-y-3.5">
              {after.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-emerald-500" style={{ fontSize: '16px' }}></i>
                  </span>
                  <span className="text-[#0d1117] text-sm leading-relaxed font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Differentiator grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          {differentiators.map((d) => (
            <div
              key={d.title}
              className="bg-[#f8faf9] border border-[#e9ecef] rounded-2xl p-6 flex gap-4 hover:border-emerald-200 hover:bg-white transition-all duration-300 group cursor-default"
            >
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 flex-shrink-0 group-hover:from-emerald-100 group-hover:border-emerald-200 transition-all duration-300">
                <i className={`${d.icon} text-emerald-600`} style={{ fontSize: '20px' }}></i>
              </div>
              <div>
                <h3 className="text-[#0d1117] font-bold text-sm md:text-base mb-1.5">{d.title}</h3>
                <p className="text-[#6b7280] text-sm leading-relaxed">{d.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Mid CTA row */}
        <div className="bg-gradient-to-r from-[#0d1117] to-[#0d2420] rounded-xl px-7 py-6 flex flex-col sm:flex-row items-center justify-between gap-5">
          <div>
            <p className="text-white font-bold text-lg mb-1">Ready to see the difference?</p>
            <p className="text-white/45 text-sm">We'll show you exactly what your new site could look like — free, before you commit.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <button
              onClick={onOpenForm}
              className="whitespace-nowrap bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-sm px-7 py-3.5 rounded-full hover:opacity-90 transition-opacity cursor-pointer"
            >
              Get Free Preview
            </button>
            <a
              href="tel:8004559670"
              className="whitespace-nowrap flex items-center gap-2 bg-white/8 border border-white/15 text-white font-semibold text-sm px-6 py-3.5 rounded-full hover:bg-white/14 transition-all cursor-pointer justify-center"
            >
              <i className="ri-phone-fill text-emerald-400" style={{ fontSize: '14px' }}></i>
              Call Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
